/**
 * Comment-target helpers for the Tonight feed. A comment's `target` (or its
 * `originTarget` when it reached a listing through a digest edge) is the
 * normalized partition id — `SHOW#1396`, `MOVIE#27205`,
 * `SHOW#1396#SEASON#2#EP#5` (`communityTargetString` in @shared/mappers).
 *
 * `threadLink` is the same destination a comment notification opens today
 * (`features/notif` → the title's page) refined for episode comments to the
 * episode's own discussion route, so "View thread" lands in the partition the
 * comment actually lives in.
 */

import type { CommunityComment, CommunityTarget } from '@shared/types';
import { titlePath } from '@/lib/title-path';

const TARGET_RE = /^(SHOW|MOVIE)#([^#]+)(?:#SEASON#(\d+)(?:#EP#(\d+))?)?$/;

export function parseCommentTarget(raw: string | null | undefined): CommunityTarget | null {
  if (!raw) return null;
  const m = TARGET_RE.exec(raw);
  if (!m) return null;
  const [, kind, tmdbId, season, episode] = m;
  if (kind === 'MOVIE') return { titleType: 'movie', tmdbId };
  if (season !== undefined && episode !== undefined) {
    return {
      titleType: 'episode',
      tmdbId,
      seasonNumber: Number(season),
      episodeNumber: Number(episode),
    };
  }
  if (season !== undefined) return { titleType: 'season', tmdbId, seasonNumber: Number(season) };
  return { titleType: 'show', tmdbId };
}

/** The partition a comment belongs to (its origin when surfaced via a digest). */
export function commentTarget(comment: CommunityComment): CommunityTarget | null {
  return parseCommentTarget(comment.originTarget ?? comment.target);
}

/** Where "View thread" goes for a comment on this target. */
export function threadLink(target: CommunityTarget | null, fallbackTitleId: string): string {
  if (!target) return `/show/${fallbackTitleId}`;
  if (
    target.titleType === 'episode' &&
    target.seasonNumber !== undefined &&
    target.episodeNumber !== undefined
  ) {
    return `/show/${target.tmdbId}/episode/${target.seasonNumber}/${target.episodeNumber}`;
  }
  return titlePath(target.tmdbId, target.titleType === 'movie' ? 'movie' : 'show');
}
